import { useEffect } from 'react'
import { Show, UserButton, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import { getActivePod } from '@/lib/pod'
import StaffSignIn from '@/components/StaffSignIn'
import { isOwnerClaim } from '@/lib/staff-role'
import { LiveCorpusSection } from '@/components/LiveCorpus'

/**
 * THE KELVO OWNER'S FACTS PAGE — /knows on a staff build (Kelvo part 2c).
 *
 * Everything the assistant is allowed to say, and the drawer each one is in.
 * The same screen as Foredesk's "What it knows" tab (src/components/LiveCorpus),
 * with drawers on: each fact says who on his staff is told it, and a fact with
 * no kept file behind it — an answer he typed to a staff question, anything
 * pasted — can be moved from here. A document's facts are moved by moving the
 * file, on /documents, and the card says so.
 *
 * NO PUBLIC PAGE ON KELVO, so no "Put on my public page". What the firm tells
 * the public is part 4 (the Website drawer), and is decided there.
 *
 * An Answered card on /questions links here as /knows#fact-<id>, and the list
 * opens at that fact.
 *
 * OWNER ONLY, ENFORCED BY THE WORKER. This page hides itself from anyone whose
 * session does not say owner, as a courtesy. The Worker's owner gate refuses
 * every /api/admin/* call from anyone else regardless of what this page shows.
 */
export default function OwnerFacts() {
  const pod = getActivePod()
  const { isLoaded, sessionClaims } = useAuth()

  useEffect(() => {
    const previous = document.title
    document.title = `${pod.brandName} — facts`
    return () => {
      document.title = previous
    }
  }, [pod.brandName])

  if (!isLoaded) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted" role="status">
          Loading…
        </p>
      </div>
    )
  }

  return (
    <>
      <Show when="signed-out">
        <StaffSignIn returnTo="/knows" />
      </Show>

      <Show when="signed-in">
        <div className="min-h-[100svh] bg-surface-base">
          <header className="border-b border-border-subtle bg-surface-raised">
            <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="font-display font-semibold leading-tight tracking-tight truncate">
                  {pod.brandName}
                </p>
                <p className="text-xs text-text-muted leading-tight">Facts</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
                >
                  Back to {pod.assistantName}
                </Link>
                <UserButton />
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-3xl px-4 pb-8">
            {isOwnerClaim(sessionClaims) ? (
              <LiveCorpusSection
                heading={`What ${pod.assistantName} knows`}
                intro={
                  <>
                    Everything you have approved, and the drawer each one is in. Your staff are
                    told only what is in the drawers their role reaches. If one has stopped being
                    true, take it out and {pod.assistantName} stops saying it straight away.
                  </>
                }
                emptyMessage="Nothing yet. Approve something on Documents, or answer a question from your staff, and it will appear here."
                drawers
                publishing={false}
                audience="staff"
              />
            ) : (
              <section className="mt-8 rounded-card border border-border-subtle bg-surface-raised p-6">
                <h1 className="font-display text-lg font-semibold tracking-tight">
                  This page is for the owner
                </h1>
                <p className="mt-2 text-sm text-text-muted">
                  What {pod.assistantName} knows is looked after by the owner of {pod.brandName}. If
                  you think you should have access, speak to the office.
                </p>
              </section>
            )}
          </main>
        </div>
      </Show>
    </>
  )
}
